import { getType } from 'typesafe-actions';
import produce from 'immer';
import * as firmwareActions from './actions';
import { ActionsOf } from '../../../helpers/actions';
import {
  STATUS_IDLE,
  STATUS_CHECKING_COMPATIBILITY,
  STATUS_COMPATIBLE,
  STATUS_INCOMPATIBLE,
  STATUS_INSTALLATION_STARTED,
  STATUS_INSTALLATION_IN_PROGRESS,
  STATUS_INSTALLATION_COMPLETE,
  STATUS_INSTALLATION_FAILED,
} from './constants';

export interface FirmwareContextState {
  updateModalState: UpdateModalState;
}

export type UpdateStatus =
  | typeof STATUS_IDLE
  | typeof STATUS_CHECKING_COMPATIBILITY
  | typeof STATUS_COMPATIBLE
  | typeof STATUS_INCOMPATIBLE
  | typeof STATUS_INSTALLATION_STARTED
  | typeof STATUS_INSTALLATION_IN_PROGRESS
  | typeof STATUS_INSTALLATION_COMPLETE
  | typeof STATUS_INSTALLATION_FAILED;

type UpdateModalState = {
  isOpen: boolean;
  checkingCompatibility: boolean;
  versions: string[];
  deviceId: number;
  selectedVersion: string | null;
  updateStatus: UpdateStatus;
  installationProgress: number;
  isForceUpdateEnabled: boolean;
  errorMessage: string | null;
};

const initialState: FirmwareContextState = {
  updateModalState: {
    isOpen: false,
    checkingCompatibility: false,
    versions: [],
    deviceId: 0,
    selectedVersion: null,
    updateStatus: STATUS_IDLE,
    installationProgress: 0,
    isForceUpdateEnabled: false,
    errorMessage: null,
  },
};

type FirmwareActions = ActionsOf<typeof firmwareActions>;

export const firmwareContextReducer = produce(
  (draft: FirmwareContextState, action: FirmwareActions) => {
    switch (action.type) {
      case getType(firmwareActions.openUpdateModal): {
        draft.updateModalState.isOpen = true;
        draft.updateModalState.deviceId = action.payload.deviceId;
        break;
      }

      case getType(firmwareActions.closeUpdateModal): {
        draft.updateModalState.isOpen = false;
        draft.updateModalState.selectedVersion = null;
        draft.updateModalState.updateStatus = STATUS_IDLE;
        draft.updateModalState.installationProgress = 0;
        draft.updateModalState.isForceUpdateEnabled = false;
        draft.updateModalState.errorMessage = null;
        break;
      }

      case getType(firmwareActions.setAvailableVersions): {
        draft.updateModalState.versions = action.payload.versions;
        break;
      }

      case getType(firmwareActions.selectVersion): {
        draft.updateModalState.selectedVersion = action.payload.version;
        break;
      }

      case getType(firmwareActions.startCompatibilityCheck): {
        draft.updateModalState.checkingCompatibility = true;
        draft.updateModalState.updateStatus = STATUS_CHECKING_COMPATIBILITY;
        break;
      }

      case getType(firmwareActions.setUpdateStatus): {
        draft.updateModalState.updateStatus = action.payload.status;
        draft.updateModalState.errorMessage = action.payload.errorMessage || null;

        if (
          draft.updateModalState.checkingCompatibility &&
          (action.payload.status === STATUS_COMPATIBLE ||
            action.payload.status === STATUS_INCOMPATIBLE)
        ) {
          draft.updateModalState.checkingCompatibility = false;
        }
        break;
      }

      case getType(firmwareActions.setForceUpdate): {
        draft.updateModalState.isForceUpdateEnabled = action.payload.enabled;
        break;
      }

      case getType(firmwareActions.startInstallation): {
        draft.updateModalState.updateStatus = STATUS_INSTALLATION_STARTED;
        draft.updateModalState.installationProgress = 0;
        break;
      }

      case getType(firmwareActions.updateInstallationProgress): {
        draft.updateModalState.installationProgress = action.payload.progress;

        if (action.payload.progress >= 100) {
          draft.updateModalState.updateStatus = STATUS_INSTALLATION_COMPLETE;
        } else if (draft.updateModalState.updateStatus !== STATUS_INSTALLATION_FAILED) {
          draft.updateModalState.updateStatus = STATUS_INSTALLATION_IN_PROGRESS;
        }
        break;
      }

      default:
        break;
    }
  },
  initialState
);
